import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useUsers from './useUsers';

const PAGE = {
  users: [{ id: 1, name: 'Abe Martinez' }],
  pagination: { page: 1, per_page: 25, total_entries: 1, total_pages: 1 },
};

// A probe rather than renderHook: @testing-library/react-hooks is not installed,
// and rendering the state is enough to assert on it. Fetches are awaited through
// waitFor rather than async act(), which React 16.8 predates.
let hook;

function Probe() {
  hook = useUsers();

  return (
    <div>
      <span data-testid="loading">{String(hook.isLoading)}</span>
      <span data-testid="error">{hook.error || ''}</span>
      <span data-testid="count">{hook.users.length}</span>
      <span data-testid="total">{hook.pagination.totalItems}</span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const respondOk = () => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok: true, json: async () => PAGE });
};

afterEach(() => {
  delete global.fetch;
  hook = undefined;
});

describe('management/Users useUsers', () => {
  it('starts loading, with nothing to show', () => {
    respondOk();

    render(<Probe />);

    expect(at('loading')).toEqual('true');
    expect(at('count')).toEqual('0');
  });

  it('exposes the rows and pagination after a successful fetch', async () => {
    respondOk();
    render(<Probe />);

    hook.fetchUsersData();

    await waitFor(() => expect(at('count')).toEqual('1'));
    expect(at('total')).toEqual('1');
    expect(at('loading')).toEqual('false');
  });

  it('forwards its arguments to the request', async () => {
    respondOk();
    render(<Probe />);

    hook.fetchUsersData({
      filters: { jobRoles: ['technician'] },
      page: 2,
      query: 'abe',
      sortBy: 'job_role',
      sortOrder: 'DESC',
    });

    await waitFor(() => expect(global.fetch).toHaveBeenCalled());

    const url = decodeURIComponent(global.fetch.mock.calls[0][0]);
    expect(url).toContain('page=2');
    expect(url).toContain('q=abe');
    expect(url).toContain('sort_by=job_role&sort_order=DESC');
    expect(url).toContain('job_roles[]=technician');
  });

  describe('when the request fails', () => {
    const respondFailing = () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });
    };

    it('reports the error', async () => {
      respondFailing();
      render(<Probe />);

      hook.fetchUsersData();

      await waitFor(() => expect(at('error')).toContain('500'));
    });

    it('stops loading', async () => {
      respondFailing();
      render(<Probe />);

      hook.fetchUsersData();

      await waitFor(() => expect(at('loading')).toEqual('false'));
    });

    // A failed refetch must not leave the previous page's rows on screen
    // alongside an error, which would misrepresent what the filters matched.
    it('clears rows left over from an earlier success', async () => {
      respondOk();
      render(<Probe />);
      hook.fetchUsersData();
      await waitFor(() => expect(at('count')).toEqual('1'));

      respondFailing();
      hook.fetchUsersData();

      await waitFor(() => expect(at('count')).toEqual('0'));
      expect(at('total')).toEqual('0');
    });

    it('clears the error on the next successful fetch', async () => {
      respondFailing();
      render(<Probe />);
      hook.fetchUsersData();
      await waitFor(() => expect(at('error')).not.toEqual(''));

      respondOk();
      hook.fetchUsersData();

      await waitFor(() => expect(at('error')).toEqual(''));
    });
  });

  // The caller drives fetching from an effect keyed on page/sort/filters, so a
  // changing identity here would loop.
  it('keeps a stable fetch function across renders', async () => {
    respondOk();
    const { rerender } = render(<Probe />);
    const first = hook.fetchUsersData;

    rerender(<Probe />);
    await waitFor(() => expect(hook.fetchUsersData).toBe(first));
  });
});
